<template>
    <div class="cz2__pets cz2__scrollbar-hide selection-category-list cz2__steps-options__list-wrapper">
        <accordion
            v-for="(group, index) in groups"
            :key="group.category"
            :accordionHeading="`${group.category} (${group.pets.length})`"
            :quantity="group.pets.length"
            :index="index.toString()"
            :class="{ 'cz2__mt-24': Object.keys(groups).length > 1 }"
        >
            <template v-slot:body>
                <ul
                    ref="selectionCardList"
                    :class="{
                        'cz2__scrollbar-margin': isKiosk && hasScrollbar,
                        'cz2__is-kiosk': isKiosk,
                    }"
                    class="cz2__scrollbar-hide selection-card-list"
                >
                    <selection-card-multi
                        v-for="p in group.pets"
                        :key="p.code"
                        :itemDescription="getPetDescription(p)"
                        :item="p"
                        :inspectOverlay="true"
                        @add="add(p)"
                        @quantityAdd="quantityAdd(p)"
                        @quantitySubtract="quantitySubtract(p)"
                        class="cz2__pet"
                    />
                </ul>
            </template>
        </accordion>
    </div>
</template>

<style lang="scss">
    #cz2.cz2 {
        .cz2__pets {
            .selection-card-list {
                padding-top: 8px;
                grid-row-gap: 40px;

                &.cz2__is-kiosk {
                    padding-left: calc(2 * var(--layout-spacing));
                    padding-right: calc(2 * var(--layout-spacing));
                }
            }
        }
    }
</style>

<script>
    import Accordion from './ui/Accordion.vue';
    import InspectModalTrigger from './InspectModalTrigger.vue';
    import SelectionCardMulti from './ui/SelectionCardMulti.vue';

    export default {
        components: {
            Accordion,
            InspectModalTrigger,
            SelectionCardMulti,
        },

        props: {
            pet: Object,
            currency: String,
            focusedIndex: Number,
        },

        watch: {
            currentGroup() {
                this.updateNavigation();
            },
        },

        computed: {
            currentGroup() {
                return this.$store.state.customizer.currentGroup;
            },

            viewController() {
                return this.$store.state.customizer.viewController;
            },

            pets() {
                return this.$store.getters.pets.map((p) => {
                    const pet = { ...p };

                    pet.price = this.formatPrice(pet.price);

                    return pet;
                });
            },

            groups() {
                let categoryOrder = [];

                if (this.viewController.blueprint.custom['fun-category-order']) {
                    categoryOrder = this.viewController.blueprint.custom['fun-category-order'].split(',').map((x) => x.trim()).filter((x) => x.length > 0);
                }

                const chunks = {};

                categoryOrder.forEach((c) => {
                    chunks[c] = {
                        category: c,
                        pets: [],
                    };
                });

                this.pets.forEach((p) => {
                    if (chunks[p.category] == null) {
                        chunks[p.category] = {
                            category: p.category,
                            pets: [],
                        };
                    }

                    chunks[p.category].pets.push(p);
                });

                // Delete any empty categories
                Object.keys(chunks).forEach((category) => {
                    if (chunks[category].pets.length === 0) {
                        delete chunks[category];
                    } else {
                        const inStockComponents = chunks[category].pets.filter((p) => !this.isOutOfStock(p.component));
                        const oosComponents = chunks[category].pets.filter((p) => this.isOutOfStock(p.component));

                        chunks[category].pets = inStockComponents.concat(oosComponents);
                    }
                });

                return chunks;
            },

            newImage() {
                return this.viewController.blueprint.custom?.['fun-new-tag-image-pets'];
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            hasScrollbar() {
                return this.$store.state.hasScrollbar;
            },
        },

        mounted() {
            this.focusOnFirstOption();
            this.updateNavigation();
        },

        methods: {
            updateNavigation(navRules) {
                this.$emit('updateNavigation', navRules);
            },

            priceValue(price) {
                if (price == null) {
                    return 0;
                }

                if (typeof price === 'number') {
                    return { value: price };
                }

                return price.find((p) => p.currencyCode === this.currency);
            },

            formatPrice(price) {
                const v = this.priceValue(price);

                if (v == null || v === 0) {
                    return this.$t('ITEM_FREE');
                }

                return this.formatPriceValue(v.value);
            },

            formatPriceValue(v, long) {
                return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency, notation: long ? 'standard' : 'compact' }).format(v);
            },

            add(pet) {
                this.$emit('add', pet);
            },

            quantityAdd(pet) {
                this.viewController.state.forEach((s) => {
                    if (s.component.placementIndependentCode === pet.placementIndependentCode) {
                        this.$store.dispatch('bumpVersion');

                        this.$store.dispatch('setComponentQuantity', {
                            component: s.component,
                            quantity: s.component.quantityMultiplier + 1,
                        });

                        this.$store.dispatch('syncPets');
                    }
                });
            },

            quantitySubtract(pet) {
                this.viewController.state.forEach((s) => {
                    if (s.component.placementIndependentCode === pet.placementIndependentCode) {
                        this.$store.dispatch('bumpVersion');

                        this.$store.dispatch('setComponentQuantity', {
                            component: s.component,
                            quantity: s.component.quantityMultiplier - 1,
                        });

                        if (s.component.quantityMultiplier === 0) {
                            const empty = s.placement.components.find((c) => c.placeholder);

                            this.viewController.updateComponent(s.placement.code, empty.code);
                        }

                        this.$store.dispatch('syncPets');
                    }
                });
            },

            getPetDescription(p) {
                return this.$t('TOTAL_ADDED').replace('{0}', p?.component?.description?.[0]?.name ?? this.$t('BUDDY'));
            },

            isOutOfStock(pet) {
                return this.viewController.isAllowed(pet) && !this.viewController.isAvailable(pet);
            },

            focusOnFirstOption() {
                const list = this.$refs.selectionCardList[0];
                const firstOption = list.querySelector('.selection-card');

                firstOption.focus();
            },
        },
    };
</script>
